/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect } from 'react'
import { Field, Form, Formik, FormikProps } from 'formik'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useAppDispatch } from 'store/hooks'
import { serviceAPI, useRenewServiceMutation } from 'api/service'
import { setInvoice } from 'store/slices/billingSlice'
import { aud } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    id: number,
    form: RefObject<FormikProps<{ billing_cycle_id: number }>>,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function RenewForm({ id, form, close }: Props) {
    const dispatch = useAppDispatch()
    const { data } = serviceAPI.endpoints.renewPrice.useQueryState(id)
    const [renewService, { isLoading, isSuccess, isError, data: invoice }] = useRenewServiceMutation()
    const renewList = data?.map(({ price, billing_cycle }) => ({
        value: billing_cycle.id,
        label: `${billing_cycle.name} - ${aud(price)}`
    }))

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if ((isSuccess || isError) && form.current) {
            form.current.setSubmitting(false)
            form.current.resetForm()
            close()

            if (invoice) {
                dispatch(setInvoice(invoice.id))
            }
        }
    }, [form, isSuccess, isError, invoice])

    return (
        <Formik
            innerRef={form}
            initialValues={{
                billing_cycle_id: data ? data[0].billing_cycle.id : 0
            }}
            onSubmit={({ billing_cycle_id }) => {
                renewService({ id, billing_cycle_id })
            }}
        >
            {() => (
                <Form id={'renewForm'}>
                    <Field
                        label={'Renewal Billing Cycle'}
                        name={'billing_cycle_id'}
                        type={'select'}
                        disabled={isLoading}
                        list={renewList}
                        component={NXUIForm.SelectField}
                    />
                </Form>
            )}
        </Formik>
    )
}
